import { datadogRum } from '@datadog/browser-rum'
import type { RumInitConfiguration, Site } from '@datadog/browser-rum'
import { GROUP_COOKIE } from '../../openfeature/cacheKey'
import { COHORT_COOKIE, COHORTS } from '../../openfeature/utils'
import type { ServerFlagState } from '../../openfeature/serverFlagClient'

/**
 * Starts Datadog RUM for the calling tenant (SUP-836).
 *
 * Inert by design unless the RUM application id and client token are present
 * in runtime config, so a build without the env vars ships no RUM and sends
 * nothing. The session sample rate is config driven because RUM is billed per
 * session; it defaults to 0.1 percent once the application is configured, and
 * an explicit 0 disables RUM entirely.
 *
 * Every tenant passes its own `service` name. That is the bucket Datadog
 * separates tenants by in the RUM explorer, dashboards and monitors, matching
 * the service names already used by Browser Logs.
 *
 * The service name is the only caller choice. Credentials, sampling, replay,
 * privacy and tracking come from runtime config and are applied after the
 * caller options, so a tenant cannot widen its sample, turn Session Replay on
 * or drop the input masking.
 *
 * Every view carries a `rendered` attribute saying whether the page arrived
 * server rendered or as the client shell, so the two paths can be compared on
 * the same day in the same dashboard. It also carries the visitor's `cohort`,
 * so a cohort held out of SSR can be compared with the ones that get it: a
 * held out cohort never gets a group cookie, so `group` alone can't find it.
 */
export default function useDatadogRumClient(
  options: Pick<RumInitConfiguration, 'service'>
): void {
  onBeforeMount(() => {
    if (!import.meta.client) return
    const config = useRuntimeConfig()

    const applicationId = config.public.datadogRumApplicationId
    const clientToken = config.public.datadogRumClientToken
    if (!applicationId || !clientToken) return

    const rawRate = config.public.datadogRumSessionSampleRate
    const sessionSampleRate =
      rawRate === undefined || rawRate === '' ? 0.1 : Number(rawRate)
    // The SDK rejects rates outside 0 to 100 and fails the whole init, so an
    // out of range value means RUM stays off, same as any other invalid config.
    if (
      !Number.isFinite(sessionSampleRate) ||
      sessionSampleRate <= 0 ||
      sessionSampleRate > 100
    ) {
      return
    }

    const site: Site =
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (config.public.datadogSite as Site) || 'us3.datadoghq.com'

    datadogRum.init({
      ...options,
      applicationId,
      clientToken,
      site,
      env: process.env.NODE_ENV,
      version: config.public.datadogVersion || 'unknown',
      sessionSampleRate,
      // Session Replay is billed separately and is out of scope for SUP-836.
      sessionReplaySampleRate: 0,
      // Interaction tracking is what produces INP and the other
      // responsiveness vitals in the field.
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
    })

    // The attribute Nuxt writes on the payload script, which is the only
    // statement in the document about how this page was produced.
    const ssr = document
      .querySelector('[data-nuxt-data]')
      ?.getAttribute('data-ssr')
    datadogRum.setGlobalContextProperty(
      'rendered',
      ssr === 'true' ? 'ssr' : 'spa'
    )
    // The group whose cached page this visitor was served, so a slow view can
    // be traced back to the render that produced it rather than to the person
    // who happened to load it. On a rendered page that is the group the server
    // actually used, not the cookie: the browser provider can rewrite the
    // cookie before this runs, and then the view would name a group that never
    // produced this HTML.
    const serverGroup = useState<ServerFlagState | null>('ssr-flags').value
      ?.context.targetingKey
    datadogRum.setGlobalContextProperty(
      'group',
      (ssr === 'true' ? serverGroup : useCookie(GROUP_COOKIE).value) || 'none'
    )
    // The OpenFeature plugin assigns the cohort before the app mounts, so the
    // cookie is set by now. Anything off the list reads as none.
    const cohort = useCookie(COHORT_COOKIE).value
    datadogRum.setGlobalContextProperty(
      'cohort',
      cohort && COHORTS.includes(cohort) ? cohort : 'none'
    )
  })
}
